import { useMemo } from 'react';
import useAccreditationVersionsAvailable from '../../_data_sources/Settings/useAccreditationVersionsAvailable';
import type { AccreditationVersion } from '../../_types/settings';

export interface EnabledVersionsInfo {
  allVersions: AccreditationVersion[];
  enabledVersions: AccreditationVersion[];
  uniqueBodiesCount: number;
  uniqueBodies: Array<{
    id: string;
    name: string;
    enabledVersions: AccreditationVersion[];
  }>;
  hasSingleEnabledVersion: boolean;
  hasSingleBody: boolean;
  hasNoEnabledVersions: boolean;
  singleVersion: AccreditationVersion | null;
  singleBody: { id: string; name: string } | null;
  isLoading: boolean;
  error: unknown;
}

export default function useEnabledVersionsInfo(organizationId: string): EnabledVersionsInfo {
  const {
    data: versionsData,
    isLoading,
    error,
  } = useAccreditationVersionsAvailable(organizationId, {
    enabled: !!organizationId,
  });

  return useMemo(() => {
    const allVersions = versionsData?.content || [];
    const enabledVersions = allVersions.filter((v) => v.accepted === 'ENABLED');

    const bodiesMap = new Map<string, { id: string; name: string; enabledVersions: AccreditationVersion[] }>();

    enabledVersions.forEach((version) => {
      const existing = bodiesMap.get(version.accreditationBodyId);
      if (existing) {
        existing.enabledVersions.push(version);
      } else {
        bodiesMap.set(version.accreditationBodyId, {
          id: version.accreditationBodyId,
          name: version.accreditationBodyName,
          enabledVersions: [version],
        });
      }
    });

    const uniqueBodies = Array.from(bodiesMap.values());
    const uniqueBodiesCount = uniqueBodies.length;
    const hasNoEnabledVersions = enabledVersions.length === 0;
    const hasSingleEnabledVersion = enabledVersions.length === 1;
    const hasSingleBody = uniqueBodiesCount === 1;

    return {
      allVersions,
      enabledVersions,
      uniqueBodiesCount,
      uniqueBodies,
      hasSingleEnabledVersion,
      hasSingleBody,
      hasNoEnabledVersions,
      singleVersion: hasSingleEnabledVersion ? enabledVersions[0] : null,
      singleBody: hasSingleBody ? { id: uniqueBodies[0].id, name: uniqueBodies[0].name } : null,
      isLoading,
      error,
    };
  }, [versionsData, isLoading, error]);
}
